import { useEffect, useRef } from "react";

import AppName from "./AppName";
import "./NewAppMigrationModal.scss";

const NEW_APP_LINKS = {
  web: "https://app.hallapp.com.br/#/login",
  ios: "https://apps.apple.com/br/app/hall-app/id6760682278",
  android:
    "https://play.google.com/store/apps/details?id=br.com.hallapp&hl=pt_BR",
};

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function WebIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16.8 12.8c0-2.4 2-3.6 2.1-3.7a4.5 4.5 0 0 0-3.5-1.9c-1.5-.2-2.9.9-3.6.9-.8 0-1.9-.9-3.1-.9a4.7 4.7 0 0 0-4 2.4c-1.7 3-.4 7.4 1.2 9.8.8 1.2 1.8 2.5 3.1 2.4 1.2 0 1.7-.8 3.3-.8 1.5 0 2 .8 3.3.8 1.4 0 2.3-1.2 3.1-2.4a10.7 10.7 0 0 0 1.4-2.9 4.2 4.2 0 0 1-2.3-3.7ZM14.4 5.6A4.2 4.2 0 0 0 15.5 2a4.3 4.3 0 0 0-2.9 1.4 4 4 0 0 0-1.1 3.4 3.6 3.6 0 0 0 2.9-1.2Z" />
    </svg>
  );
}

function AndroidIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m4 3 11.8 9L4 21V3Zm12.6 9.7 2.8 2.1-3.2 1.8-2.5-1.9 2.9-2Zm-.4-5.3 3.2 1.8-2.8 2.1-2.9-2 2.5-1.9Z" />
    </svg>
  );
}

export default function NewAppMigrationModal() {
  const dialogRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    const keepFocusInside = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll("a[href]");
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", keepFocusInside, true);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", keepFocusInside, true);
    };
  }, []);

  return (
    <div className="migration-gate" role="presentation">
      <div className="migration-gate__glow migration-gate__glow--top" />
      <div className="migration-gate__glow migration-gate__glow--bottom" />

      <section
        ref={dialogRef}
        className="migration-gate__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="migration-title"
        aria-describedby="migration-description"
        tabIndex="-1"
      >
        <div className="migration-gate__hero">
          <div className="migration-gate__brand" aria-hidden="true">
            <AppName width="34px" color="#ffffff" />
          </div>
          <span className="migration-gate__eyebrow">O NOVO HALLAPP CHEGOU</span>
          <h1 id="migration-title">Estamos de cara nova<span>!</span></h1>
          <p id="migration-description">
            Evoluímos para oferecer uma experiência mais rápida, moderna e
            completa para o seu negócio.
          </p>
        </div>

        <div className="migration-gate__content">
          <div className="migration-gate__notice">
            <span aria-hidden="true">✦</span>
            <p>
              Esta versão foi descontinuada. Para continuar usando o HallApp,
              escolha abaixo onde deseja acessar a nova versão.
            </p>
          </div>

          <a className="migration-gate__web" href={NEW_APP_LINKS.web}>
            <span className="migration-gate__button-icon"><WebIcon /></span>
            <span>
              <strong>Acessar pela web</strong>
              <small>Abra o novo HallApp no navegador</small>
            </span>
            <span className="migration-gate__arrow"><ArrowIcon /></span>
          </a>

          <div className="migration-gate__divider">
            <span>ou baixe o aplicativo</span>
          </div>

          <div className="migration-gate__stores">
            <a href={NEW_APP_LINKS.ios} aria-label="Baixar o HallApp para iPhone na App Store">
              <span className="migration-gate__store-icon"><AppleIcon /></span>
              <span><small>Baixar na</small><strong>App Store</strong></span>
            </a>
            <a href={NEW_APP_LINKS.android} aria-label="Baixar o HallApp para Android no Google Play">
              <span className="migration-gate__store-icon"><AndroidIcon /></span>
              <span><small>Disponível no</small><strong>Google Play</strong></span>
            </a>
          </div>

          <p className="migration-gate__footer">
            Sua conta e seus dados continuam seguros e disponíveis na nova versão.
          </p>
        </div>
      </section>
    </div>
  );
}
